import React from 'react';
import { Calendar, AlertCircle, DollarSign, Users } from 'lucide-react';

const HolidayCalendar = ({ holidays, compact = false }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: compact ? 'short' : 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    const holidayDate = new Date(dateString);
    const diffTime = holidayDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil < 0) return 'bg-gray-100 text-gray-600';
    if (daysUntil <= 7) return 'bg-red-100 text-red-700';
    if (daysUntil <= 30) return 'bg-orange-100 text-orange-700';
    return 'bg-blue-100 text-blue-700';
  };

  if (!holidays || holidays?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No upcoming public holidays</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {holidays?.map((holiday) => {
        const daysUntil = getDaysUntil(holiday?.date);
        const urgencyColor = getUrgencyColor(daysUntil);

        return (
          <div
            key={holiday?.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {/* Holiday Name */}
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {holiday?.name}
                    </h3>
                  </div>

                  {/* Date */}
                  <p className="text-gray-600 mb-3">
                    {formatDate(holiday?.date)}
                  </p>

                  {/* Description */}
                  {holiday?.description && !compact && (
                    <p className="text-sm text-gray-600 mb-4">
                      {holiday?.description}
                    </p>
                  )}

                  {/* Payroll Impact */}
                  {!compact && holiday?.payrollImpact && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-blue-900">Payroll Impact: </span>
                          <span className="text-blue-700">{holiday?.payrollImpact}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Leave Scheduling Recommendation */}
                  {!compact && holiday?.leaveRecommendation && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <span className="font-medium text-green-900">Leave Scheduling: </span>
                          <span className="text-green-700">{holiday?.leaveRecommendation}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Days Until Badge */}
                {daysUntil !== null && daysUntil >= 0 && (
                  <div className={`flex-shrink-0 px-4 py-2 rounded-lg text-center ${urgencyColor}`}>
                    <div className="text-2xl font-bold">{daysUntil}</div>
                    <div className="text-xs font-medium">days</div>
                  </div>
                )}
              </div>

              {/* Alert for Upcoming Holidays */}
              {daysUntil !== null && daysUntil > 0 && daysUntil <= 14 && !compact && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <span className="font-medium">Action Required: </span>
                      Ensure payroll processing is completed before this holiday. Review leave requests and staffing requirements.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default HolidayCalendar;